import React, {useContext, useEffect, useState} from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import FIND_COTTAGES from "../../graphql/queries/cottage";
import USER from "../../graphql/queries/user";
import withApollo from "../../lib/withApollo";
import {useRouter} from "next/router";
import {useQuery} from "@apollo/react-hooks";
import {getDataFromTree} from "@apollo/react-ssr";
import {Badge, Breadcrumb, BreadcrumbItem, Button, Col, Container, Form, FormGroup, Row,} from "reactstrap";
import UseWindowSize from "../../hooks/UseWindowSize";
import Head from "next/head";
import moment from "moment";
import PriceIncludesInfoBlock from "../../components/PriceIncludesInfoBlock";
import SwiperGallery from "../../components/Cottage/SwiperGallery";
import CalendarModal from "../../components/Cottage/Mobile/CalendarModal";
import CottageDetailHeader from "../../components/Header/CottageDetail/Mobile/Index";
import Header from "../../components/Header";
import BookingButtons from "../../components/Cottage/Mobile/BookingButtons";
import CardOverlayHearth from "../../components/Favourites/CardOverlayHearth";
import {FilterSelection} from "../../types/filterSelection";
import AddToCartButton from "../../components/Button/AddToCartButton";
import LargeScreenComponent from "../../components/LargeScreenComponent";
import {useLazyQuery} from "@apollo/client";
import {getSessionId} from "../../services/session";
import {Context} from "../../lib/Store";
import SmallScreenComponent from "../../components/SmallScreenComponent";

type UrlQuery = {
    'aankomst-datum': string | undefined,
    'eind-datum': string | undefined,
    nachten: number | undefined,
    'aantal-volwassenen': number | undefined,
    'aantal-kinderen': number | undefined,
    'aantal-babies': number | undefined,
    'aantal-honden': number | undefined
}

const AddToCartButtonComponent = React.forwardRef((props: any, ref) => (
    <AddToCartButton {...props} />
));

const formatForSwiper = (data) => {
    const images = data.images.filter(image => image.online).map((image) => {
        return {
            img: image.url,
            title: 'Cottage Centre',
            alt: 'Cottage Centre',
        };
    });

    if (images.length < 3) {
        return [
            ...images,
            ...images
        ]
    }

    return images;
};

const encodeBookingDetails = (details) => {
    return Buffer.from(JSON.stringify(details)).toString("base64");
};

const findPriceForRange = (availability, searchSelection: FilterSelection) => {
    const {startDate, endDate, getAmountOfNights} = searchSelection;
    if (!dateRangeSelected(searchSelection)) {
        return null;
    }

    const nights = getAmountOfNights();

    const date = availability.availableDates.find(
        (date) => date.date === startDate
    );

    if (!date) {
        return null;
    }

    const nightsItem = date.availableNights.find(
        (availableNightsItem) => availableNightsItem.nights === nights
    );

    if (!nightsItem) {
        return null;
    }

    return {
        price: nightsItem.price,
        nights: nights,
    };
};

const renderPricingForDate = (
    availability,
    searchSelection: FilterSelection,
    popoverOpen,
    toggle,
    query,
    cottage
) => {
    const price = findPriceForRange(availability, searchSelection);
    const {startDate, endDate} = searchSelection;

    return (
        <>
            <h6>Prijs</h6>
            <Row>
                <Col lg="8">{price.nights} nachten</Col>
                <Col lg="4">
                    <span className="float-right">&euro; {price.price}</span>
                </Col>
                <Col lg="8">Overige kosten</Col>
                <Col lg="4">
                    <span className="float-right">&euro; 0</span>
                </Col>
            </Row>
            <hr className="my-4"/>
            <Row>
                <Col lg="8">
                    <span className="h6">Totaal</span>
                </Col>
                <Col lg="4">
                    <span className="h6 float-right">&euro; {price.price}</span>
                </Col>
            </Row>
            <Row>
                <Col lg="12">
                    {cottage.petsAllowed && !cottage.availabilityOnRequest &&
                        <div className="mt-2"><i className="fas fa-paw"/> Extra per hond € 25,-<br/>
                            Wordt verrekend tijdens het boeken.
                        </div>
                    }
                </Col>
            </Row>
            <p>
                {cottage.availabilityOnRequest
                    ?
                    <>
                        <span className="font-weight-bold red">Beschikbaarheid is op aanvraag</span>
                        <Link
                            href={`/aanvraag/beschikbaarheid/[slug]`}
                            as={`/aanvraag/beschikbaarheid/${query.slug}?aankomst-datum=${startDate}&eind-datum=${endDate}`}
                            passHref
                        >
                            <Button
                                aria-label="Beschikbaarheid opvragen"
                                className="book-button mt-2"
                                color="primary"
                                block
                            >
                                Beschikbaarheid opvragen
                            </Button>
                        </Link>

                    </>
                    :
                    <Link
                        href={`/boeken/[details]`}
                        as={`/boeken/${encodeBookingDetails({
                            slug: query.slug,
                            startDate: startDate
                                ? startDate
                                : null,
                            endDate: endDate ? endDate : null,
                            adults: searchSelection.adults,
                            children: searchSelection.children,
                            babies: searchSelection.babies,
                            dogs: searchSelection.dogs
                        })}`}
                        passHref
                    >
                        <AddToCartButtonComponent
                            cottageId={cottage.id}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    </Link>
                }
            </p>

            <FormGroup>
                <p className="text-sm text-center no-charges-text">
                    Er wordt nog niets in rekening gebracht
                </p>
            </FormGroup>
        </>
    );
};

const getDefaultSelectedNights = (availableNights) => {
    let week = availableNights.find((availableNight) => availableNight === 7);

    return week !== undefined ? week : availableNights[0];
};

const initialRange = [{startDate: moment()}, {endDate: null}];

let Map;
let DateRangePicker = dynamic(
    () => import("../../components/Cottage/DateRangePicker"),
    {ssr: false}
);

let LastViewedCottages = dynamic(
    () => import("../../components/LastViewedCottages"),
    {ssr: false}
);

let Gallery = dynamic(() => import("../../components/Cottage/Gallery"), {
    ssr: false,
});

const dateRangeSelected = (filterSelection: FilterSelection): boolean => {
    return filterSelection.startDate !== null && filterSelection.endDate !== null;
}

const Index = (props) => {
    const [state, dispatch] = useContext(Context);
    const [referrerIsSameDomain, setReferrerIsSameDomain] = useState(false);
    const sessionId = getSessionId();
    const [lastSelectedSearchFilter, setLastSelectedSearchFilter] = React.useState(new FilterSelection());
    const [range, setRange] = React.useState(initialRange);
    const [mapLoaded, setMapLoaded] = React.useState(false);
    const [dragging, setDragging] = React.useState(false);
    const [tap, setTap] = React.useState(false);
    const [searchSelection, setSearchSelection] = useState((new FilterSelection()).serialize);
    const [price, setPrice] = useState(null);
    const size = UseWindowSize();
    const router = useRouter();
    const query = router.query;
    const [bookingDetails, setBookingDetails] = useState({slug: query.slug});
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [selectedNights, setSelectedNights] = useState<number>(null);
    const [dateFocused, setDateFocused] = React.useState(null);
    const toggle = () => setPopoverOpen(!popoverOpen);
    const [modal, setModal] = useState(false);
    const filterSelection: FilterSelection = FilterSelection.fromUrlParams(router);
    const [showLoadingScreen, setShowLoadingScreen] = useState(false);
    const toggleModal = () => {
        setShowLoadingScreen(!showLoadingScreen);
    };

    useEffect(() => {
        setModal(showLoadingScreen);
    }, [showLoadingScreen]);

    const [getUserData] = useLazyQuery(USER, {
        onCompleted: data => setLastSelectedSearchFilter(FilterSelection.deserialize(
                {
                    ...data.user.lastSelectedSearchFilter,
                    showMap: query['toon-kaart']
                }
            )
        )
    })

    const {loading, error, data} = useQuery(FIND_COTTAGES, {
        variables: {
            slug: query.slug,
        },
    });

    const goBack = () => {
        const routes = state.slice();
        // @ts-ignore
        const currentRoute = routes.pop();
        // @ts-ignore
        const urlToNavigateTo = routes.pop() ?? '/';
        // @ts-ignore
        dispatch({type: 'navigate_back'})

        if (!urlToNavigateTo.includes('vakantiehuis')) {
            router.replace('/vakantiehuis');
        } else {
            router.back();
        }
    }


    useEffect(() => {
        const searchSelectionObject = FilterSelection.deserialize(searchSelection)
        if (dateRangeSelected(searchSelectionObject) && data) {
            setPrice(findPriceForRange(data.cottage.availability, searchSelectionObject));
        }
    }, [searchSelection, data]);

    React.useEffect(() => {
        setReferrerIsSameDomain(document.referrer.indexOf(location.protocol + "//" + location.host) === 0);
        getUserData({
            variables: {
                uuid: sessionId
            }
        });
        Map = dynamic(() => import("../../components/Map"), {ssr: false});
        setMapLoaded(true);

        setTap(size.width > 700 ? true : false);
        setDragging(size.width > 700 ? true : false);
    }, []);

    React.useEffect(() => {
        const searchSelection = FilterSelection.fromUrlParams(router);
        const selectedNightsFromRange = searchSelection.getAmountOfNights();

        setSearchSelection(searchSelection.serialize);

        if (router.query.nachten) {
            setSelectedNights(parseInt(router.query['nachten'].toString()));
            return;
        }

        if (!selectedNights && selectedNightsFromRange) {
            setSelectedNights(selectedNightsFromRange);
            return;
        }

        if (!selectedNights && data && data.cottage && data.cottage.id) {
            setSelectedNights(
                getDefaultSelectedNights(data.cottage.availableNightsList)
            );
        }
    }, [query, data]);

    const updateUrl = (range, selectedNights) => {
        let urlQuery: UrlQuery = {
            'aankomst-datum': undefined,
            'eind-datum': undefined,
            nachten: undefined,
            'aantal-volwassenen': searchSelection.adults,
            'aantal-kinderen': searchSelection.children,
            'aantal-babies': searchSelection.babies,
            'aantal-honden': searchSelection.dogs,
        };

        if (range.startDate && range.endDate) {
            urlQuery['aankomst-datum'] = range.startDate.format("YYYY-MM-DD");
            urlQuery['eind-datum'] = range.endDate.format("YYYY-MM-DD");
        }

        if (selectedNights) {
            urlQuery.nachten = selectedNights;
        }

        router.replace(
            {
                pathname: "/vakantiehuizen/[slug]",
                query: urlQuery,
            },
            {
                pathname: `/vakantiehuizen/${data.cottage.slug}`,
                query: urlQuery,
            },
            {
                shallow: true,
            }
        );
    };

    const updateRange = (range) => {
        setRange(range);
        updateUrl(range, selectedNights);
    };

    const onChangeSelectedNights = (selectedNights) => {
        setRange(initialRange);
        setSelectedNights(selectedNights);
        updateUrl(initialRange, selectedNights);
    };

    if (!data) {
        return <h1>Cottage gegevens aan het laden...</h1>;
    }

    const cottage = data.cottage;
    const swiperData = formatForSwiper(cottage);

    const groupByN = (n, data) => {
        let result = [];
        for (let i = 0; i < data.length; i += n) result.push(data.slice(i, i + n));
        return result;
    };

    const facilities = cottage.attributes.filter(
        (attribute) => attribute.category === "Faciliteiten"
    );
    const activities = cottage.attributes.filter(
        (attribute) => attribute.category === "Activiteiten"
    );
    const position = cottage.attributes.filter(
        (attribute) => attribute.category === "Ligging"
    );

    return (
        <React.Fragment>
            <Head>
                <title>
                    {`${cottage.maxPersons} persoons, ${cottage.bedrooms} slaapkamer vakantiehuis in ${cottage.county} ${cottage.country}`}{" "}
                    - Cottage Centre Vakantiehuizen
                </title>
                <link rel="canonical" href={`https://www.cottagecentre.nl/vakantiehuizen/${cottage.slug}`}/>
                <meta name="viewport" content="initial-scale=1.0, width=device-width"/>
                <meta
                    name="description"
                    content={`${cottage.maxPersons} persoons, ${cottage.bedrooms} slaapkamer vakantiehuis in ${cottage.county} ${cottage.country}`}
                />
            </Head>

            <div className="d-none d-lg-block">
                <Header  {...props} />
            </div>

            <div className="d-lg-none">
                <CottageDetailHeader
                    price={price}
                    toggleCalendarModal={toggleModal}
                />
            </div>

            <section>
                <div className="position-relative">
                    {swiperData && <SwiperGallery data={swiperData}/>}
                    <CardOverlayHearth
                        cottageId={cottage.id}
                    />
                </div>

                <Container className="py-4 d-lg-none">
                    {price && FilterSelection.deserialize(searchSelection).hasRangeSelection() &&
                        <BookingButtons
                            searchSelection={FilterSelection.deserialize(searchSelection)}
                            encodedBookingDetails={encodeBookingDetails({
                                slug: query.slug,
                                startDate: searchSelection.startDate
                                    ? searchSelection.startDate
                                    : null,
                                endDate: searchSelection.endDate ? searchSelection.endDate : null,
                                adults: searchSelection.adults,
                                children: searchSelection.children,
                                babies: searchSelection.babies,
                                dogs: searchSelection.dogs
                            })}
                            price={price}
                            cottageId={cottage.id}
                            availabilityOnRequest={cottage.availabilityOnRequest}
                            slug={query.slug.toString()}
                        />
                    }

                    {!price &&
                        <>
                            {data.cottage.availabilityOnRequest || !data.cottage.lowestWeekPrice
                                ?
                                <>
                                    <b className="red">Beschikbaarheid/Prijs is op
                                        aanvraag</b>
                                    <Link
                                        href={`/aanvraag/beschikbaarheid/[slug]`}
                                        as={`/aanvraag/beschikbaarheid/${query.slug}`}
                                        passHref
                                    >
                                        <Button
                                            size="sm"
                                            aria-label="Beschikbaarheid en prijs opvragen"
                                            className="book-button mt-2"
                                            color="primary"
                                            block
                                        >
                                            <>
                                                Beschikbaarheid/Prijs opvragen
                                            </>
                                        </Button>
                                    </Link>
                                </>
                                :
                                <Button
                                    onClick={() => toggleModal()}
                                    size="sm"
                                    aria-label="Boek cottage"
                                    className="book-button"
                                    color="primary"
                                    block
                                >
                                    Bekijk beschikbaarheid en prijs
                                    {" "}
                                    <i className="fas fa-calendar-alt"/>
                                </Button>
                            }
                        </>
                    }
                </Container>


                <Container className="py-lg-5">
                    <Row>
                        <Col lg="8">
                            <LargeScreenComponent>
                                <Row>
                                    <Breadcrumb>
                                        <BreadcrumbItem><a href="#">Home</a></BreadcrumbItem>
                                        <BreadcrumbItem>
                                            <>
                                                <a href="#" onClick={() => goBack()}>Zoekresultaten</a>
                                            </>
                                        </BreadcrumbItem>
                                        <BreadcrumbItem>Cottage {cottage.name} {cottage.internalCode}</BreadcrumbItem>
                                    </Breadcrumb>
                                </Row>
                            </LargeScreenComponent>

                            <div className="text-block pt-1">
                                <p className="text-primary">
                                    <i className="fa-map-marker-alt fa mr-1"/>
                                    &nbsp;{`${cottage.county}, ${cottage.country}`}
                                </p>
                                {cottage.name && <h1>{cottage.name}</h1>}
                                {cottage.category && (
                                    <div className="text-muted text-uppercase mb-4">
                                        Vakantiehuis
                                    </div>
                                )}

                                <SmallScreenComponent>
                                    <div className="mb-3">
                                        <span className="small">Huiscode: {cottage.internalCode}</span>
                                    </div>
                                </SmallScreenComponent>

                                {cottage.tags && (
                                    <ul className="list-inline text-sm mb-4">
                                        {cottage.tags.map((tag) => (
                                            <li key={tag.value} className="list-inline-item mr-3">
                                                <i
                                                    className={`fa fa-${tag.icon} mr-1 text-secondary`}
                                                />{" "}
                                                {tag.value}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {cottage.isDutchDescriptionAutoTranslated &&
                                    <p className="font-italic small font-weight-bold">
                                        Onderstaande omschrijving is geautomatiseerd vertaald en nog niet door ons
                                        persoonlijk gecontroleerd.<br/>
                                        <br/>
                                        Om u alvast wel volledige informatie over de cottage te kunnen geven, hebben wij
                                        voor deze automatische vertaling met dus mogelijk taalfouten of foutieve
                                        zinsopbouw gekozen. Op deze manier kunnen we u veel meer cottages
                                        aanbieden.<br/>
                                        Wij proberen deze tekst zo snel mogelijk te hebben gecorrigeerd.<br/>
                                    </p>
                                }
                                <p
                                    dangerouslySetInnerHTML={{__html: cottage.dutchDescription}}
                                    className=""
                                ></p>
                            </div>
                            {facilities && (
                                <React.Fragment>
                                    <div className="text-block">
                                        <h4 className="mb-0">Faciliteiten</h4>
                                        <p className="subtitle text-sm text-primary mb-4">
                                        </p>
                                            <ul className="list-inline">
                                                {facilities.map((amenity) => (
                                                    <li
                                                        key={amenity.name}
                                                        className="list-inline-item mb-2"
                                                    >
                                                        <Badge
                                                            pill
                                                            color="light"
                                                            className="p-3 text-muted font-weight-normal"
                                                        >
                                                            {amenity.name}
                                                        </Badge>
                                                    </li>
                                                ))}
                                            </ul>
                                    </div>
                                </React.Fragment>
                                )}

                            {activities.length > 0 && (
                                <React.Fragment>
                                    <div className="text-block">
                                        <h4 className="mb-0">Activiteiten</h4>
                                        <p className="subtitle text-sm text-primary mb-4">
                                            {/* Activiteiten in de omgeving */}
                                        </p>
                                        <ul className="list-inline">
                                            {activities.map((amenity) => (
                                                <li
                                                    key={amenity.name}
                                                    className="list-inline-item mb-2"
                                                >
                                                    <Badge
                                                        pill
                                                        color="light"
                                                        className="p-3 text-muted font-weight-normal"
                                                    >
                                                        {amenity.name}
                                                    </Badge>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </React.Fragment>
                            )}

                            {position && (
                                <React.Fragment>
                                    <div className="text-block">
                                        <h4 className="mb-0">Ligging</h4>
                                        <p className="subtitle text-sm text-primary mb-4">
                                            {/* Ligging en gelegenheden in de buurt */}
                                        </p>
                                        <ul className="list-inline">
                                            {position.map((amenity) => (
                                                <li
                                                    key={amenity.name}
                                                    className="list-inline-item mb-2"
                                                >
                                                    <Badge
                                                        pill
                                                        color="light"
                                                        className="p-3 text-muted font-weight-normal"
                                                    >
                                                        {amenity.name}{" "}
                                                        {amenity.value &&
                                                            `${amenity.value.replace(".", ",")} km`}
                                                    </Badge>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </React.Fragment>
                            )}

                            <div className="text-block">
                                <h3 className="mb-4">Locatie</h3>
                                <div className="map-wrapper-300 mb-3">
                                    {mapLoaded && cottage.coordinates && (
                                        <Map
                                            className="h-100"
                                            center={[
                                                cottage.coordinates.lat,
                                                cottage.coordinates.lon,
                                            ]}
                                            circlePosition={[
                                                cottage.coordinates.lat,
                                                cottage.coordinates.lon,
                                            ]}
                                            circleRadius={500}
                                            zoom={14}
                                            dragging={dragging}
                                            tap={tap}
                                        />
                                    )}
                                </div>
                            </div>

                            {swiperData && (
                                <div className="text-block">
                                    <h4 className="mb-0">Foto's</h4>
                                    <p className="subtitle text-sm text-primary mb-4">
                                        Klik op een afbeelding om deze te vergroten
                                    </p>
                                    <Gallery
                                        rowClasses="ml-n1 mr-n1"
                                        lg="4"
                                        xs="6"
                                        colClasses="px-1 mb-2"
                                        data={swiperData}
                                    />
                                </div>
                            )}

                            <div className="text-block">
                                <h4 className="mb-0">GarantieRegeling</h4>
                                <p className="subtitle text-sm text-primary mb-4">
                                    {/* Ligging en gelegenheden in de buurt */}
                                </p>
                                <p>
                                    Vormt uw boeking bij Cottage Centre een pakketreis (bijvoorbeeld overtocht +
                                    cottage), dan valt uw boeking onder de garantieregeling van STO Garant.
                                </p>
                            </div>

                        </Col>
                        <Col lg="4">
                            <div
                                style={{top: "100px"}}
                                className="p-4 shadow ml-lg-4 rounded sticky-top d-none d-lg-block"
                            >
                                <Form
                                    id="booking-form"
                                    method="get"
                                    action="#"
                                    autoComplete="off"
                                    className="form"
                                >
                                    {cottage.lowestWeekPrice &&
                                        <FormGroup>
                                            <DateRangePicker
                                                dateFocused={dateFocused}
                                                setDateFocused={setDateFocused}
                                                availableNights={cottage.availableNightsList}
                                                selectedNights={selectedNights}
                                                setSelectedNights={(nights) =>
                                                    onChangeSelectedNights(nights)
                                                }
                                                range={range}
                                                setRange={updateRange}
                                                searchSelection={FilterSelection.deserialize(searchSelection)}
                                                availability={cottage.availability}
                                            />
                                        </FormGroup>
                                    }

                                    {cottage.lowestWeekPrice &&
                                        <PriceIncludesInfoBlock cottage={data.cottage}/>
                                    }

                                    <div className="text-sm">
                                        {(FilterSelection.deserialize(searchSelection)).hasRangeSelection() &&
                                            findPriceForRange(data.cottage.availability, FilterSelection.deserialize(searchSelection)) &&
                                            renderPricingForDate(
                                                data.cottage.availability,
                                                FilterSelection.deserialize(searchSelection),
                                                popoverOpen,
                                                toggle,
                                                query,
                                                cottage
                                            )}
                                        {!findPriceForRange(data.cottage.availability, FilterSelection.deserialize(searchSelection)) && (
                                            <>

                                                {cottage.lowestWeekPrice &&
                                                    <>
                                                        <span className="h3">
                                                          €{cottage.lowestWeekPrice}-€{cottage.highestWeekPrice}
                                                        </span>{" "}per week
                                                    </>
                                                }

                                                {data.cottage.petsAllowed &&
                                                    <div className="mt-2"><i className="fas fa-paw"/> Extra per hond €
                                                        25,-</div>
                                                }

                                                <div className="request-price-button">
                                                    {data.cottage.availabilityOnRequest
                                                        ?
                                                        <>
                                                            {cottage.lowestWeekPrice ?
                                                                <b className="red">Beschikbaarheid is op aanvraag</b>
                                                                :
                                                                <b className="red">Beschikbaarheid/Prijs is op
                                                                    aanvraag</b>
                                                            }
                                                            <Link
                                                                href={`/aanvraag/beschikbaarheid/[slug]`}
                                                                as={`/aanvraag/beschikbaarheid/${query.slug}`}
                                                                passHref
                                                            >
                                                                <Button
                                                                    aria-label="Beschikbaarheid en prijs opvragen"
                                                                    className="book-button mt-2"
                                                                    color="primary"
                                                                    block
                                                                >
                                                                    <>
                                                                        Beschikbaarheid/Prijs opvragen
                                                                    </>
                                                                </Button>
                                                            </Link>
                                                        </>
                                                        :
                                                        <>
                                                            {cottage.lowestWeekPrice ?
                                                                <Button
                                                                    onClick={() => setDateFocused('startDate')}
                                                                    aria-label="Beschikbaarheid en prijs controleren"
                                                                    className="book-button"
                                                                    color="primary"
                                                                    block
                                                                >
                                                                    Beschikbaarheid/Prijs controleren
                                                                </Button>
                                                                :
                                                                <>
                                                                    <b className="red">Beschikbaarheid/Prijs is op
                                                                        aanvraag</b>
                                                                    <Link
                                                                        href={`/aanvraag/beschikbaarheid/[slug]`}
                                                                        as={`/aanvraag/beschikbaarheid/${query.slug}`}
                                                                        passHref
                                                                    >
                                                                        <Button
                                                                            aria-label="Beschikbaarheid en prijs opvragen"
                                                                            className="book-button mt-2"
                                                                            color="primary"
                                                                            block
                                                                        >
                                                                            <>
                                                                                Beschikbaarheid/Prijs opvragen
                                                                            </>
                                                                        </Button>
                                                                    </Link>
                                                                </>
                                                            }
                                                        </>
                                                    }
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Container>
                {/* <span className="fixed-bottom back-to-search-button">
          <BackToSearchButton />
        </span> st*/}
            </section>

            {/*{data.cottage && data.cottage.id && (*/}
            {/*    <section className="py-6 bg-gray-100">*/}
            {/*        <Container>*/}
            {/*            <h5 className="mb-0">Laatst bekeken cottage's</h5>*/}
            {/*            <p className="subtitle text-sm text-primary mb-4">*/}
            {/*                Swipe voor meer*/}
            {/*            </p>*/}
            {/*            <LastViewedCottages*/}
            {/*                className="swiper-container items-slider pb-5"*/}
            {/*                perView={1}*/}
            {/*                spaceBetween={20}*/}
            {/*                loop={false}*/}
            {/*                roundLengths={true}*/}
            {/*                md={2}*/}
            {/*                lg={3}*/}
            {/*                xl={4}*/}
            {/*                cottageId={data.cottage.id}*/}
            {/*                cards={true}*/}
            {/*            />*/}
            {/*        </Container>*/}
            {/*    </section>*/}
            {/*)}*/}

            <CalendarModal
                // @ts-ignore
                lowestWeekPrice={data.cottage.lowestWeekPrice}
                highestWeekPrice={data.cottage.highestWeekPrice}
                searchSelection={FilterSelection.deserialize(searchSelection)}
                price={price}
                slug={query.slug}
                availability={cottage.availability}
                availableNights={cottage.availableNightsList}
                selectedNights={selectedNights}
                setSelectedNights={(nights) =>
                    onChangeSelectedNights(nights)
                }
                setRange={updateRange}
                modal={modal}
                toggle={toggleModal}
            />
            {showLoadingScreen &&
                <div className="navigation-loader z-index-200"></div>
            }
        </React.Fragment>
    );
};

Index.getInitialProps = async () => {
    return {
        // hideMobileBottomMenu: true,
        nav: {
            light: true,
            classes: "shadow",
            color: "white",
        },
        showBackButton: true,
        paddingTop: '70px',
        hideHeader: true,
    };
};

export default withApollo(Index, {getDataFromTree});
